import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface MonitoringLog {
  id: number;
  demarche_id: number;
  user_id: number;
  type_verification: string;
  ancien_statut: string | null;
  nouveau_statut: string | null;
  details: string | null;
  error_message: string | null;
  created_at: string;
  demarche_titre?: string;
  client_nom?: string;
}

interface MonitoringStats {
  total_demarches_monitored: number;
  verifications_24h: number;
  changements_detectes_24h: number;
  erreurs_24h: number;
}

const MonitoringDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<MonitoringLog[]>([]);
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningMonitoring, setRunningMonitoring] = useState(false);
  const [filterType, setFilterType] = useState<string>('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, [filterType, limit]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/monitoring/statistics');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: any = { limit };
      if (filterType) params.type = filterType;

      const response = await api.get('/monitoring/logs', { params });
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunMonitoring = async () => {
    if (!window.confirm('Lancer le monitoring maintenant pour toutes les démarches actives ?')) {
      return;
    }

    setRunningMonitoring(true);
    try {
      await api.post('/monitoring/run');
      alert('Monitoring démarré en arrière-plan. Les résultats apparaîtront dans les logs d\'ici quelques minutes.');

      // Refresh logs after a delay
      setTimeout(() => {
        fetchLogs();
        fetchStats();
      }, 3000);
    } catch (error: any) {
      console.error('Erreur lancement monitoring:', error);
      alert(error.response?.data?.message || 'Erreur lors du lancement du monitoring');
    } finally {
      setRunningMonitoring(false);
    }
  };

  const getTypeColor = (type: string) => {
    const colors: any = {
      sync_success: 'success',
      status_change: 'warning',
      new_notification: 'info',
      complement_demande: 'warning',
      temp_account_empty: 'error',
      connection_error: 'error',
      auth_error: 'error',
      no_change: 'default',
    };
    return colors[type] || 'default';
  };

  const getTypeLabel = (type: string) => {
    const labels: any = {
      sync_success: 'Synchro réussie',
      status_change: 'Changement statut',
      new_notification: 'Nouvelle notification',
      complement_demande: 'Demande complément',
      temp_account_empty: 'Compte temporaire vide',
      connection_error: 'Erreur connexion',
      auth_error: 'Erreur authentification',
      no_change: 'Aucun changement',
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    if (type.includes('error')) return <ErrorIcon fontSize="small" />;
    if (type === 'temp_account_empty') return <WarningIcon fontSize="small" />;
    if (type === 'sync_success') return <CheckCircleIcon fontSize="small" />;
    return <InfoIcon fontSize="small" />;
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Monitoring ANEF</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchLogs();
                fetchStats();
              }}
            >
              Actualiser
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={runningMonitoring ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRunMonitoring}
              disabled={runningMonitoring}
            >
              {runningMonitoring ? 'En cours...' : 'Lancer monitoring maintenant'}
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Le monitoring automatique s'exécute 3 fois par jour à 8h00, 14h00 et 20h00 (heure de Paris).
          Vous pouvez également le déclencher manuellement avec le bouton ci-dessus.
        </Alert>

        {/* Statistiques */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Démarches monitorées
                  </Typography>
                  <Typography variant="h4">{stats.total_demarches_monitored}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Vérifications (24h)
                  </Typography>
                  <Typography variant="h4">{stats.verifications_24h}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Changements détectés (24h)
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.changements_detectes_24h}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Erreurs (24h)
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.erreurs_24h}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filtres */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type de vérification</InputLabel>
                <Select
                  value={filterType}
                  label="Type de vérification"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="sync_success">Synchro réussie</MenuItem>
                  <MenuItem value="status_change">Changement statut</MenuItem>
                  <MenuItem value="new_notification">Nouvelle notification</MenuItem>
                  <MenuItem value="complement_demande">Demande complément</MenuItem>
                  <MenuItem value="temp_account_empty">Compte temporaire vide</MenuItem>
                  <MenuItem value="connection_error">Erreur connexion</MenuItem>
                  <MenuItem value="auth_error">Erreur authentification</MenuItem>
                  <MenuItem value="no_change">Aucun changement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Nombre de résultats"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                InputProps={{ inputProps: { min: 10, max: 500 } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tableau des logs */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Démarche</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Détails</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucun log de monitoring
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </TableCell>
                      <TableCell>{log.demarche_titre || `Démarche #${log.demarche_id}`}</TableCell>
                      <TableCell>{log.client_nom || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(log.type_verification)}
                          label={getTypeLabel(log.type_verification)}
                          color={getTypeColor(log.type_verification)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.ancien_statut && log.nouveau_statut ? (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {log.ancien_statut}
                            </Typography>
                            <Typography variant="caption"> → </Typography>
                            <Typography variant="caption" color="primary">
                              {log.nouveau_statut}
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {log.error_message ? (
                          <Typography variant="caption" color="error">
                            {log.error_message}
                          </Typography>
                        ) : log.details ? (
                          <Typography variant="caption" color="text.secondary">
                            {typeof log.details === 'string'
                              ? log.details
                              : JSON.stringify(log.details).substring(0, 100)}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Voir la démarche">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/demarches/${log.demarche_id}`)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Layout>
  );
};

export default MonitoringDashboard;
